/**
 * Utility functions for Public Recording feature
 */

import type {
  JobResult,
  ScribeJob,
  ValidationResult,
} from '@/types/public-recording';
import { envConfig } from './env-config';
import { supabase } from './supabase';

/**
 * Validates the integration partner token by calling the gateway API
 * @param partnerId - The partner ID from Redux store
 * @param accessKey - The access key (integration key) from query params
 * @param partnerName - The partner name from query params
 * @returns Promise<ValidationResult> - Validation result with optional error message
 */
export const validateIntegrationKey = async (
  partnerId: string,
  accessKey: string,
  partnerName: string,
): Promise<ValidationResult> => {
  const startTime = Date.now();
  console.log('[Integration Key Validation] Starting validation...');

  if (!accessKey) {
    console.error('[Integration Key Validation] Missing access key');
    return { valid: false, error: 'Access key is required' };
  }

  if (!partnerName) {
    console.error('[Integration Key Validation] Missing partner name');
    return { valid: false, error: 'Partner name is required' };
  }

  try {
    const validationUrl = `${envConfig.gateway.baseUrl}/api/integration/validate`;

    // Get the Supabase session token for authenticated requests
    // const {
    //   data: { session },
    // } = await supabase.auth.getSession();

    // if (!session?.access_token) {
    //   console.error('[Integration Key Validation] No valid session found');
    //   return { valid: false, error: 'Authentication session not found' };
    // }

    console.log('[Integration Key Validation] Calling validation service...', {
      partnerId: partnerId,
      partnerName: partnerName,
      accessKeyPreview: '***' + accessKey.slice(-4),
    });

    const response = await fetch(validationUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Authorization: `Bearer ${session.access_token}`,
        'X-Integration-Key': accessKey,
      },
      body: JSON.stringify({
        partnerName: partnerName,
      }),
    });

    const responseTime = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Integration Key Validation] Validation failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        responseTime: `${responseTime}ms`,
      });
      return {
        valid: false,
        error: `Validation failed: ${response.statusText}`,
      };
    }

    const result = await response.json();

    if (result.valid || result.success) {
      console.log('[Integration Key Validation] Validation successful:', {
        partnerId: partnerId,
        responseTime: `${responseTime}ms`,
        hasToken: !!result.token,
      });
      return {
        valid: true,
        token: result.token,
      };
    } else {
      console.error('[Integration Key Validation] Validation failed:', {
        error: result.error || result.message || 'Invalid credentials',
        responseTime: `${responseTime}ms`,
      });
      return {
        valid: false,
        error:
          result.error || result.message || 'Invalid partner ID or access key',
      };
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    console.error(
      '[Integration Key Validation] Validation error:',
      errorMessage,
    );
    return {
      valid: false,
      error: `Validation service error: ${errorMessage}`,
    };
  }
};

/**
 * Look up or create a scribe job based on appointment details
 * @returns Promise<JobResult> - Job ID or error message
 */
export const getOrCreateScribeJob = async (
  appointmentId: string,
  patientId: string,
  locationId: string,
  sectionTag?: string,
  patientFirstName?: string,
  patientLastName?: string,
  patientDob?: string,
): Promise<JobResult> => {
  const logPrefix = '[Scribe Job]';

  try {
    console.log(`${logPrefix} Looking up job with:`, {
      appointmentId,
      patientId,
      locationId,
      sectionTag: sectionTag || 'none',
    });

    // First, try to find an existing job
    const { data: existingJobs, error: queryError } = await supabase
      .from('ev_scribe_jobs')
      .select('*')
      .eq('appointment_id', appointmentId)
      .eq('patient_id', patientId)
      .eq('location_id', locationId)
      .limit(1);

    if (queryError) {
      console.error(`${logPrefix} Database query error:`, {
        message: queryError.message,
        code: queryError.code,
        details: queryError.details,
      });
      return {
        jobId: null,
        error: `Database error: ${queryError.message}`,
      };
    }

    // If found, return the existing job ID
    if (existingJobs && existingJobs.length > 0) {
      const existingJob = existingJobs[0] as ScribeJob;
      console.log(`${logPrefix} ✅ Found existing job:`, existingJob.id);
      return { jobId: existingJob.id };
    }

    // If not found, create a new job
    console.log(`${logPrefix} No existing job found. Creating new job...`);
    const newJob = {
      appointment_id: appointmentId,
      patient_id: patientId,
      location_id: locationId,
      status: 'CREATED',
      appointment_start_time: new Date().toISOString(),
      pt_first_name: patientFirstName || '',
      pt_last_name: patientLastName || '',
      pt_dob: patientDob || '01/01/2000',
      pt_gender: '',
      pt_email: '',
      pt_phone_number: '',
      service: '',
      created_by: 'Public Ai Scribe',
    };

    const { data: createdJob, error: createError } = await supabase
      .from('ev_scribe_jobs')
      .insert(newJob)
      .select()
      .single();

    if (createError) {
      console.error(`${logPrefix} Failed to create job:`, {
        message: createError.message,
        code: createError.code,
        details: createError.details,
      });
      return {
        jobId: null,
        error: `Failed to create recording session: ${createError.message}`,
      };
    }

    if (!createdJob) {
      console.error(`${logPrefix} No data returned after creating job`);
      return {
        jobId: null,
        error: 'Failed to create recording session (no data returned)',
      };
    }

    const createdScribeJob = createdJob as ScribeJob;
    console.log(`${logPrefix} ✅ Created new job:`, createdScribeJob.id);
    return { jobId: createdScribeJob.id };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    console.error(`${logPrefix} Unexpected error:`, errorMessage, error);
    return {
      jobId: null,
      error: `Unexpected error: ${errorMessage}`,
    };
  }
};
